// @oasis-permission-inherits: /ai-edits/evals (evals:view)
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import { Badge } from "~/components/ui/badge";
import { Skeleton } from "~/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  buildChatRunComparisons,
  shortAgaveEvalSha,
  shortChatRunLabel,
  type ChatEvalRunComparison,
} from "~/modules/agave-evals/chat-comparisons";
import {
  AgaveEvalExternalButton,
  AgaveEvalGoalBlock,
  AgaveEvalRenderedProjectPanel,
  AgaveEvalSourceBadge,
} from "~/modules/agave-evals/components";
import { AgaveDatasetBrowser } from "~/modules/agave-evals/dataset-browser";
import { formatAgaveEvalDate } from "~/modules/agave-evals/formatters";
import {
  agaveEvalCreationWorkflowForListKind,
  agaveEvalListKindLabel,
  agaveEvalListKindSchema,
  agaveEvalProfileForListKind,
  agaveEvalSuiteForListKind,
  isChatSuiteEvalListKind,
  isCreationSuiteEvalListKind,
  type AgaveEvalListKind,
} from "~/modules/agave-evals/list-kinds";
import { MetricRubricColumns } from "~/modules/agave-evals/metric-rubric";
import { AgaveOptimizationOverview } from "~/modules/agave-evals/optimization-overview";
import {
  fetchAgaveCreationLiveEvalsQuery,
  fetchAgaveEvalComparisonsQuery,
  fetchAgaveEvalRunsQuery,
} from "~/modules/agave-evals/queries";
import { sameAgaveRunPair } from "~/modules/agave-evals/run-history";
import { AgaveEvalRunTable, AgaveEvalRunsSkeleton } from "~/modules/agave-evals/run-table";
import type {
  AgaveCreationLiveEvalScore,
  AgaveCreationLiveEvalStatus,
  AgaveCreationLiveEvalSummary,
  AgaveEvalComparisonSummary,
  AgaveEvalRunSummary,
} from "~/modules/agave-evals/schemas";

const CHAT_EVAL_RUN_PAGE_SIZE = 25;
const HISTORICAL_EVAL_RUN_LIMIT = 100;
const PAGINATED_EVAL_RUN_LIMIT = 50;

const searchSchema = z.object({
  kind: agaveEvalListKindSchema.optional().default("chat"),
  afterCursor: z.string().optional(),
  comparisonAfterCursor: z.string().optional(),
  legacy: z.boolean().optional().default(false),
  baselineRunId: z.string().min(1).optional(),
  candidateRunId: z.string().min(1).optional(),
  comparisonId: z.string().min(1).optional(),
  datasetId: z.string().min(1).optional(),
  datasetRunId: z.string().min(1).optional(),
  taskId: z.string().min(1).optional(),
  reviewCase: z.string().min(1).optional(),
  baselineTrial: z.number().int().min(1).optional(),
  candidateTrial: z.number().int().min(1).optional(),
  reviewSide: z.enum(["baseline", "candidate"]).optional(),
  optimizationView: z
    .enum(["overview", "compare", "datasets", "benchmark", "cost", "quality", "runs", "archived"])
    .optional()
    .default("overview"),
});

export const Route = createFileRoute("/_dash/ai-edits/evals/")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

function resolveOptimizationView(
  view: z.infer<typeof searchSchema>["optimizationView"]
): "overview" | "compare" | "archived" {
  if (view === "archived") {
    return "archived";
  }
  if (view === "compare" || view === "cost" || view === "quality") {
    return "compare";
  }
  return "overview";
}

function RouteComponent() {
  const {
    legacy,
    kind,
    optimizationView,
    baselineRunId,
    candidateRunId,
    comparisonId,
    datasetId,
    datasetRunId,
    taskId,
  } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  if (legacy || kind === "creation-live") {
    return <LegacyRouteComponent />;
  }
  if (optimizationView === "datasets") {
    return <AgaveDatasetBrowser datasetId={datasetId} runId={datasetRunId} taskId={taskId} />;
  }
  return (
    <AgaveOptimizationOverview
      initialView={resolveOptimizationView(optimizationView)}
      initialBaselineRunId={baselineRunId}
      initialCandidateRunId={candidateRunId}
      initialComparisonId={comparisonId}
      onComparisonRunIdsChange={(baseline, candidate) =>
        void navigate({
          search: (previous) => ({
            ...previous,
            optimizationView: "compare",
            baselineRunId: baseline || undefined,
            candidateRunId: candidate || undefined,
            comparisonId: sameAgaveRunPair(
              previous.baselineRunId,
              previous.candidateRunId,
              baseline,
              candidate
            )
              ? previous.comparisonId
              : undefined,
          }),
        })
      }
    />
  );
}

function LegacyRouteComponent() {
  const { kind, afterCursor, comparisonAfterCursor, legacy } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const isLiveKind = kind === "creation-live";
  const isChatSuiteKind = isChatSuiteEvalListKind(kind);
  const isCreationSuiteKind = isCreationSuiteEvalListKind(kind);
  const isPaginatedEvalKind = kind === "ai-edit-fx" || isCreationSuiteKind;
  const hasStoredComparisons = kind === "harbor-chat" || kind === "creation" || isPaginatedEvalKind;
  const runKind = isChatSuiteKind ? "chat" : "creation";
  const runListLimit = runLimitForKind(kind);
  const evalProfile = agaveEvalProfileForListKind(kind);
  const evalSuite = agaveEvalSuiteForListKind(kind);
  const creationWorkflow = agaveEvalCreationWorkflowForListKind(kind);
  const { data, isLoading, error } = fetchAgaveEvalRunsQuery.useQuery(
    {
      params: {
        kind: runKind,
        evalProfile,
        evalSuite,
        creationWorkflow,
        limit: runListLimit,
        afterCursor: isChatSuiteKind || isPaginatedEvalKind ? afterCursor : undefined,
      },
    },
    { enabled: !isLiveKind }
  );
  const {
    data: comparisonsData,
    isLoading: comparisonsLoading,
    error: comparisonsError,
  } = fetchAgaveEvalComparisonsQuery.useQuery(
    {
      params: {
        evalProfile,
        evalSuite,
        creationWorkflow,
        limit: 25,
        afterCursor: comparisonAfterCursor,
      },
    },
    { enabled: hasStoredComparisons }
  );
  const {
    data: liveData,
    isLoading: liveLoading,
    error: liveError,
  } = fetchAgaveCreationLiveEvalsQuery.useQuery(
    {
      params: {
        workflowName: "EditPlanToProjectRenderSampleWorkflow",
        limit: 50,
      },
    },
    { enabled: isLiveKind, refetchInterval: 10_000 }
  );
  const runs = data?.runs ?? [];
  const comparisons = hasStoredComparisons ? (comparisonsData?.comparisons ?? []) : [];
  const liveEvals = isLiveKind ? (liveData?.liveEvals ?? []) : [];
  const itemCount = isLiveKind ? liveEvals.length : runs.length;
  const pageInfo = data?.pageInfo;
  const comparisonPageInfo = comparisonsData?.pageInfo;
  const hasNextChatPage = pageInfo?.hasNextPage === true && !!pageInfo.endCursor;
  const hasNextRunPage = pageInfo?.hasNextPage === true && !!pageInfo.endCursor;
  const hasNextComparisonPage =
    comparisonPageInfo?.hasNextPage === true && !!comparisonPageInfo.endCursor;
  const showChatPager =
    isChatSuiteKind && !isLoading && !error && (!!afterCursor || hasNextChatPage);
  const showPaginatedEvalPager =
    isPaginatedEvalKind && !isLoading && !error && (!!afterCursor || hasNextRunPage);
  return (
    <div className="space-y-8">
      <nav className="flex gap-4 text-sm" aria-label="Evaluation views">
        <Link
          to="/ai-edits/evals"
          search={{ optimizationView: "overview" }}
          className="text-primary hover:underline"
        >
          All evaluation runs
        </Link>
        <Link
          to="/ai-edits/evals"
          search={{
            kind: "harbor-chat",
            optimizationView: "runs",
            legacy: true,
          }}
          className="text-primary hover:underline"
        >
          Harbor chat runs and comparisons
        </Link>
      </nav>
      <div className="space-y-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold">Evaluation runs</h2>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <span>
                {itemCount} {isLiveKind ? "videos" : "runs"}
              </span>
              <span>·</span>
              <span>{agaveEvalListKindLabel(kind)}</span>
            </div>
          </div>
          <Tabs
            value={kind}
            onValueChange={(next) =>
              navigate({
                search: (previous) => ({
                  ...previous,
                  kind: next as AgaveEvalListKind,
                }),
              })
            }
          >
            <TabsList className="h-auto flex-wrap justify-end">
              <TabsTrigger value="harbor-chat" className="text-xs">
                Harbor Chat
              </TabsTrigger>
              <TabsTrigger value="chat" className="text-xs">
                Chat Regular
              </TabsTrigger>
              <TabsTrigger value="chat-mfx" className="text-xs">
                Chat MFX
              </TabsTrigger>
              <TabsTrigger value="chat-creation" className="text-xs">
                Chat Creation
              </TabsTrigger>
              <TabsTrigger value="creation" className="text-xs">
                AI Edit
              </TabsTrigger>
              <TabsTrigger value="ai-edit-fx" className="text-xs">
                AI Edit FX
              </TabsTrigger>
              <TabsTrigger value="creation-live" className="text-xs">
                AI Edit (Live)
              </TabsTrigger>
              <TabsTrigger value="fx-creation" className="text-xs">
                Creation Regular
              </TabsTrigger>
              <TabsTrigger value="fx-creation-mfx" className="text-xs">
                Creation MFX
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {isLiveKind ? (
          <CreationLiveFeed liveEvals={liveEvals} error={liveError} isLoading={liveLoading} />
        ) : (
          <>
            {isLoading && <AgaveEvalRunsSkeleton />}

            {error && (
              <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
                <p className="font-medium">Failed to load eval runs</p>
                <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
              </div>
            )}

            {hasStoredComparisons && (
              <>
                <RecentComparisons
                  comparisons={comparisons}
                  sourceKind={kind}
                  error={comparisonsError}
                  isLoading={comparisonsLoading}
                  legacy={legacy}
                />
                {(comparisonAfterCursor || hasNextComparisonPage) && (
                  <div className="flex items-center justify-end gap-2">
                    {comparisonAfterCursor && (
                      <button
                        type="button"
                        className="text-sm text-primary hover:underline"
                        onClick={() =>
                          navigate({
                            search: (previous) => ({
                              ...previous,
                              kind,
                              afterCursor,
                              comparisonAfterCursor: undefined,
                            }),
                          })
                        }
                      >
                        Newest comparisons
                      </button>
                    )}
                    {hasNextComparisonPage && (
                      <button
                        type="button"
                        className="text-sm text-primary hover:underline"
                        onClick={() =>
                          navigate({
                            search: (previous) => ({
                              ...previous,
                              kind,
                              afterCursor,
                              comparisonAfterCursor: comparisonPageInfo?.endCursor ?? undefined,
                            }),
                          })
                        }
                      >
                        Older comparisons
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {isChatSuiteKind && kind !== "harbor-chat" && runs.length > 1 && (
              <ChatRecentComparisons legacy={legacy} runs={runs} sourceKind={kind} />
            )}

            {!isLoading && !error && runs.length === 0 && (
              <div className="rounded-md border p-5 text-sm text-muted-foreground">
                No runs found.
              </div>
            )}

            {runs.length > 0 && <AgaveEvalRunTable legacy={legacy} runs={runs} sourceKind={kind} />}

            {showChatPager && (
              <EvalRunPagination
                afterCursor={afterCursor}
                endCursor={hasNextChatPage ? pageInfo.endCursor : null}
                kind={kind}
                legacy={legacy}
              />
            )}

            {showPaginatedEvalPager && (
              <EvalRunPagination
                afterCursor={afterCursor}
                comparisonAfterCursor={comparisonAfterCursor}
                endCursor={hasNextRunPage ? pageInfo.endCursor : null}
                kind={kind}
                legacy={legacy}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function runLimitForKind(kind: AgaveEvalListKind) {
  if (isChatSuiteEvalListKind(kind)) {
    return CHAT_EVAL_RUN_PAGE_SIZE;
  }
  if (kind === "ai-edit-fx" || isCreationSuiteEvalListKind(kind)) {
    return PAGINATED_EVAL_RUN_LIMIT;
  }
  return HISTORICAL_EVAL_RUN_LIMIT;
}

function RecentComparisons({
  comparisons,
  sourceKind,
  error,
  isLoading,
  legacy,
}: {
  comparisons: AgaveEvalComparisonSummary[];
  sourceKind: AgaveEvalListKind;
  error: Error | null;
  isLoading: boolean;
  legacy: boolean;
}) {
  if (isLoading) {
    return (
      <section className="space-y-2">
        <h2 className="text-base font-semibold">Recent comparisons</h2>
        <Skeleton className="h-20 w-full rounded-md" />
      </section>
    );
  }

  if (error) {
    return (
      <section className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
        <p className="font-medium">Failed to load eval comparisons</p>
        <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
      </section>
    );
  }

  if (comparisons.length === 0) {
    return null;
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Recent comparisons</h2>
        <div className="text-xs text-muted-foreground">{comparisons.length} comparisons</div>
      </div>
      <div className="overflow-hidden rounded-md border">
        <div className="grid grid-cols-[minmax(220px,1.5fr)_140px_140px_160px] gap-3 border-b bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground">
          <div>Comparison</div>
          <div>Source</div>
          <div>Overall</div>
          <div>Links</div>
        </div>
        <div className="divide-y">
          {comparisons.map((comparison) => (
            <ComparisonRow
              key={comparison.comparisonId}
              comparison={comparison}
              legacy={legacy}
              sourceKind={sourceKind}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ChatRecentComparisons({
  legacy,
  runs,
  sourceKind,
}: {
  legacy: boolean;
  runs: AgaveEvalRunSummary[];
  sourceKind: AgaveEvalListKind;
}) {
  const comparisons = buildChatRunComparisons(runs).slice(0, 25);

  if (comparisons.length === 0) {
    return null;
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold">Recent comparisons</h2>
        <div className="text-xs text-muted-foreground">{comparisons.length} comparisons</div>
      </div>
      <div className="overflow-hidden rounded-md border">
        <div className="grid grid-cols-[minmax(220px,1.5fr)_140px_140px_160px] gap-3 border-b bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground">
          <div>Comparison</div>
          <div>Source</div>
          <div>Overall</div>
          <div>Links</div>
        </div>
        <div className="divide-y">
          {comparisons.map((comparison) => (
            <ChatComparisonRow
              key={`${comparison.baseline.runId}->${comparison.candidate.runId}`}
              comparison={comparison}
              legacy={legacy}
              sourceKind={sourceKind}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function CreationLiveFeed({
  liveEvals,
  error,
  isLoading,
}: {
  liveEvals: AgaveCreationLiveEvalSummary[];
  error: Error | null;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <AgaveEvalRunsSkeleton />;
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
        <p className="font-medium">Failed to load live evals</p>
        <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
      </div>
    );
  }

  if (liveEvals.length === 0) {
    return (
      <div className="rounded-md border p-5 text-sm text-muted-foreground">
        No live evals found.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {liveEvals.map((liveEval) => (
        <LiveEvalCard key={liveEval.liveEvalId} liveEval={liveEval} />
      ))}
    </div>
  );
}

function LiveEvalCard({ liveEval }: { liveEval: AgaveCreationLiveEvalSummary }) {
  const videoUrl = liveEval.videoAsset?.url ?? null;
  const videoAssetId = liveEval.videoAssetId ?? liveEval.videoAsset?.assetId ?? null;
  const visibleScores = liveEval.scores.length > 0 ? liveEval.scores : fallbackScores(liveEval);
  const renderedProject = {
    projectDataUri: liveEval.projectDataUri ?? null,
    videoAssetId,
    error: liveEval.error ?? null,
  };

  return (
    <article className="grid gap-4 rounded-md border bg-background p-4 md:grid-cols-[240px_minmax(0,1fr)]">
      <AgaveEvalRenderedProjectPanel
        label="Generated"
        project={renderedProject}
        compact
        assetUri={videoUrl}
        emptyText={liveEval.status === "running" ? "Rendering" : "No video"}
      />
      <div className="min-w-0 space-y-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <LiveStatusBadge status={liveEval.status} />
              <span className="font-mono text-xs text-muted-foreground">
                {formatAgaveEvalDate(liveEval.createdAt)}
              </span>
              {liveEval.sampleLabel && (
                <Badge variant="outline" className="text-xs">
                  {sampleLabel(liveEval.sampleLabel)}
                </Badge>
              )}
            </div>
            <div className="mt-2 truncate font-mono text-xs text-foreground">
              {liveEval.liveEvalId}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {liveEval.sourceOperationId && <span>operation {liveEval.sourceOperationId}</span>}
              {liveEval.persistedEditPlanId && <span>plan {liveEval.persistedEditPlanId}</span>}
              {liveEval.judgeModel && <span>{liveEval.judgeModel}</span>}
              {liveEval.rubricVersion && <span>{liveEval.rubricVersion}</span>}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-1.5">
            <AgaveEvalExternalButton href={videoUrl} label="Video" size="compact" />
          </div>
        </div>

        <AgaveEvalGoalBlock goal={liveEval.goal} />

        <MetricRubricColumns
          metrics={liveEval.metrics}
          scores={visibleScores}
          emptyText="No live metrics scored yet."
        />

        {(liveEval.error || liveEval.judgeError) && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {liveEval.error ?? liveEval.judgeError}
          </div>
        )}
      </div>
    </article>
  );
}

function LiveStatusBadge({ status }: { status: AgaveCreationLiveEvalStatus }) {
  let variant: "default" | "destructive" | "outline" = "default";
  if (status === "failed") {
    variant = "destructive";
  } else if (status === "running") {
    variant = "outline";
  }

  return (
    <Badge variant={variant} className="text-xs">
      {status}
    </Badge>
  );
}

function ComparisonRow({
  comparison,
  legacy,
  sourceKind,
}: {
  comparison: AgaveEvalComparisonSummary;
  legacy: boolean;
  sourceKind: AgaveEvalListKind;
}) {
  return (
    <div className="grid grid-cols-[minmax(220px,1.5fr)_140px_140px_160px] items-center gap-3 px-4 py-3 text-sm">
      <div className="min-w-0">
        <Link
          to="/ai-edits/evals/comparisons/$comparisonId"
          params={{ comparisonId: comparison.comparisonId }}
          search={{ kind: sourceKind, legacy }}
          className="truncate font-mono text-xs text-primary hover:underline"
        >
          {comparison.comparisonId}
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{formatAgaveEvalDate(comparison.createdAt)}</span>
          <span className="font-mono">
            {shortAgaveEvalSha(comparison.baselineGitSha)} {"->"}{" "}
            {shortAgaveEvalSha(comparison.candidateGitSha)}
          </span>
          {comparison.prNumber && <span>PR #{comparison.prNumber}</span>}
        </div>
      </div>
      <div>
        <AgaveEvalSourceBadge source={comparison.runSource} />
      </div>
      <div className="font-mono text-xs">{formatOverallDelta(comparison)}</div>
      <div className="flex flex-wrap gap-1.5">
        <AgaveEvalExternalButton href={comparison.baselineOasisUrl} label="Base" size="compact" />
        <AgaveEvalExternalButton href={comparison.candidateOasisUrl} label="Head" size="compact" />
        <AgaveEvalExternalButton href={comparison.workflowUrl} label="Logs" size="compact" />
      </div>
    </div>
  );
}

function ChatComparisonRow({
  comparison,
  legacy,
  sourceKind,
}: {
  comparison: ChatEvalRunComparison;
  legacy: boolean;
  sourceKind: AgaveEvalListKind;
}) {
  const { baseline, candidate } = comparison;

  return (
    <div className="grid grid-cols-[minmax(220px,1.5fr)_140px_140px_160px] items-center gap-3 px-4 py-3 text-sm">
      <div className="min-w-0">
        <Link
          to="/ai-edits/evals/$runId"
          params={{ runId: candidate.runId }}
          search={{ kind: sourceKind, legacy }}
          className="truncate font-mono text-xs text-primary hover:underline"
        >
          {shortChatRunLabel(baseline)} {"->"} {shortChatRunLabel(candidate)}
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{formatAgaveEvalDate(candidate.createdAt)}</span>
          {candidate.prNumber && <span>PR #{candidate.prNumber}</span>}
          <span>
            {baseline.casesPassedAllTrials}/{baseline.caseCount} {"->"}{" "}
            {candidate.casesPassedAllTrials}/{candidate.caseCount} cases
          </span>
        </div>
      </div>
      <div>
        <AgaveEvalSourceBadge source={candidate.runSource} />
        {!candidate.runSource && (
          <Badge variant="outline" className="text-xs">
            chat
          </Badge>
        )}
      </div>
      <div className="font-mono text-xs">{formatRunPairDelta(comparison)}</div>
      <div className="flex flex-wrap gap-1.5">
        <InternalEvalButton
          runId={baseline.runId}
          label="Base"
          legacy={legacy}
          sourceKind={sourceKind}
        />
        <InternalEvalButton
          runId={candidate.runId}
          label="Head"
          legacy={legacy}
          sourceKind={sourceKind}
        />
        <AgaveEvalExternalButton href={candidate.workflowUrl} label="Logs" size="compact" />
      </div>
    </div>
  );
}

function InternalEvalButton({
  runId,
  label,
  legacy,
  sourceKind,
}: {
  runId: string;
  label: string;
  legacy: boolean;
  sourceKind: AgaveEvalListKind;
}) {
  return (
    <Link
      to="/ai-edits/evals/$runId"
      params={{ runId }}
      search={{ kind: sourceKind, legacy }}
      className="inline-flex h-7 items-center rounded-md border border-input bg-background px-2 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
    >
      {label}
    </Link>
  );
}

function EvalRunPagination({
  afterCursor,
  comparisonAfterCursor,
  endCursor,
  kind,
  legacy,
}: {
  afterCursor: string | undefined;
  comparisonAfterCursor?: string;
  endCursor: string | null | undefined;
  kind: AgaveEvalListKind;
  legacy: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-2">
      {afterCursor && (
        <Link
          to="/ai-edits/evals"
          search={(previous) => ({
            ...previous,
            kind,
            legacy,
            afterCursor: undefined,
            comparisonAfterCursor,
          })}
          className="inline-flex h-8 items-center rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
        >
          First page
        </Link>
      )}
      {endCursor && (
        <Link
          to="/ai-edits/evals"
          search={(previous) => ({
            ...previous,
            kind,
            legacy,
            afterCursor: endCursor,
            comparisonAfterCursor,
          })}
          className="inline-flex h-8 items-center rounded-md border border-input bg-background px-3 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
        >
          Next page
        </Link>
      )}
    </div>
  );
}

function formatOverallDelta(comparison: AgaveEvalComparisonSummary) {
  const all = comparison.overall.all;
  const delta =
    all && typeof all === "object" && "delta" in all && typeof all.delta === "number"
      ? all.delta
      : null;
  if (delta == null) {
    return "n/a";
  }
  const sign = delta >= 0 ? "+" : "";
  return `${sign}${(delta * 100).toFixed(1)} pp`;
}

function formatRunPairDelta(comparison: ChatEvalRunComparison) {
  const baselineScore = getRunCaseScore(comparison.baseline);
  const candidateScore = getRunCaseScore(comparison.candidate);
  const delta = candidateScore - baselineScore;
  const sign = delta >= 0 ? "+" : "";
  return `${sign}${(delta * 100).toFixed(1)} pp`;
}

function getRunCaseScore(run: AgaveEvalRunSummary) {
  return run.caseCount > 0 ? run.casesPassedAllTrials / run.caseCount : 0;
}

function sampleLabel(value: string) {
  return value.replace("edit_plan_to_project_non_production_", "").replaceAll("_", " ");
}

function fallbackScores(liveEval: AgaveCreationLiveEvalSummary): AgaveCreationLiveEvalScore[] {
  return liveEval.metricIds.map((metricId) => ({
    metricId,
    scoreType: "unknown",
    value: null,
    passed: liveEval.failedMetricIds.includes(metricId) ? false : null,
    metadata: {},
  }));
}
